import {
  H2,
  Alert,
  Button,
  Text,
  TextLink,
  Flex,
  IconButton,
  UpdaterHeader,
} from '@updater/ui-uds';
import { Form, FormProvider, SuperSchema } from '@updater/ui-informant';
import { ArrowLeft } from '@phosphor-icons/react';
import styled from '@emotion/styled';
import React, { useCallback, useState } from 'react';
import { useRESTClient } from 'context';
import { MovingAppNextPage } from 'types';
import { useWindowDimensions } from 'flows/core/hooks/use-window-dimensions';
import { useRouter } from 'next/router';
import { EMAIL_REGX } from 'constants/regx';

const schema: SuperSchema = {
  type: 'object',
  required: ['email'],
  properties: {
    email: {
      title: 'email',
      type: 'string',
      pattern: EMAIL_REGX,
      ui: {
        label: 'Email',
        inputType: 'email',
        messages: {
          required: 'Please enter your email address',
          pattern: 'Please enter a valid email address',
        },
      },
    },
  },
};

const useSendEmailPWResetLink = () => {
  const { axios } = useRESTClient();
  const sendEmailPWResetLink = useCallback(
    (data: Partial<FormData>) => {
      return axios.post('v2/password_resets', data).then((res) => res.data);
    },
    [axios]
  );

  return {
    sendEmailPWResetLink,
  };
};

interface FormData {
  email: string;
}
type ContainerTypes = {
  deviceHeight: number;
  isSmallDevice: boolean;
};

const getContainerHeight = ({
  deviceHeight,
  isSmallDevice,
}: ContainerTypes): string => {
  if (isSmallDevice) {
    return '100%';
  }
  return `${deviceHeight}px`;
};
const Container = styled(Flex)<ContainerTypes>`
  min-height: ${({ deviceHeight, isSmallDevice }) =>
    getContainerHeight({ deviceHeight, isSmallDevice })};
  width: 100vw;
  overflow: scroll;
  overflow-x: hidden;
`;
interface CustomSubmitButtonProps {
  message: string;
  onClick?: () => void;
}

const CustomSubmitButton = ({ message, onClick }: CustomSubmitButtonProps) => {
  return (
    // Negative margin accounts for informant-ui's default bottom padding...
    <Button mt="-2rem" type="submit" size="l" isFluidWidth onClick={onClick}>
      {message}
    </Button>
  );
};

export const ForgotPassword: MovingAppNextPage = () => {
  const router = useRouter();
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [emailSubmitted, setSubmittedEmail] = useState('');
  const { height, isSmallDevice } = useWindowDimensions();
  const { sendEmailPWResetLink } = useSendEmailPWResetLink();
  const initialValues: FormData = {
    email: emailSubmitted,
  };

  const handleSubmit = async (values: FormData) => {
    try {
      setSubmittedEmail(values.email);
      setEmailSent(true);
      await sendEmailPWResetLink(values);
      if (emailError) setEmailError(undefined);
    } catch (error) {
      setEmailError(
        "We couldn't find this email. Please check the email and try again."
      );
    }
  };

  return (
    <Container
      flexDirection="column"
      deviceHeight={height}
      isSmallDevice={isSmallDevice}
    >
      <UpdaterHeader
        data-testid="header"
        onLogoClick={() => router.push('/')}
        menuRequired={false}
      />
      <Flex
        flexDirection="column"
        alignItems="center"
        mt={['xxxl']}
        flexGrow="1"
        marginLeft={['s', 's', 's', 's']}
        marginRight={['s', 's', 's', 's']}
      >
        <Flex
          flexDirection="column"
          width="100%"
          maxWidth="490px"
          mt={['s', 's', 'xxxl', 'xxxl']}
        >
          <IconButton
            data-testid="back-button"
            icon={<ArrowLeft />}
            aria-label="back to login"
            onClick={() => router.push('/')}
            variant="secondary"
          />

          <H2
            textAlign="left"
            as="h2"
            mt="m"
            mb="m"
            variant={['xxl', 'xxxl', 'xxxl', 'xxxl']}
          >
            {emailSent
              ? 'Reset link sent to your email'
              : 'Reset your password'}
          </H2>
          {emailSent ? (
            <>
              <Alert
                variant="success"
                mb="xs"
                message="Please check your email for a password reset link if your account is valid."
              />

              <Text variant="s" mb="m">
                If you don&apos;t see the email within a few minutes, please
                check your spam or junk folder.
              </Text>

              <FormProvider
                schema={schema}
                onSubmit={handleSubmit}
                initialValues={initialValues}
                ctaMode="enabled"
                submitComponent={<CustomSubmitButton message="Resend Email" />}
              >
                <Form />
              </FormProvider>
            </>
          ) : (
            <>
              <Text variant="m" mb="m">
                No problem. We&apos;ll send you an email to reset your password.
              </Text>
              <FormProvider
                schema={schema}
                onSubmit={handleSubmit}
                ctaMode="enabled"
                submitComponent={<CustomSubmitButton message="Send Email" />}
              >
                <Form />
              </FormProvider>
            </>
          )}
        </Flex>
        <Text variant="s" pt="m" pb="m">
          Need help? Contact us at {` `}
          <TextLink
            showTextDecoration
            variant="s"
            href="mailto:Help@updater.com"
          >
            Help@updater.com
          </TextLink>
          .
        </Text>
      </Flex>
    </Container>
  );
};

ForgotPassword.allowUnauthorized = true;
export default ForgotPassword;
